import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api';
import { ErrorState, Loading, StatusPill } from '../../ui/bits';
import { Icon } from '../../ui/Icon';
import { Fig, Sheet } from '../../ui/viz';
import { count, peso } from '../../lib/format.js';
import type { OpsRider } from '../queries';
import { profileFacts, profileView, riderProfileKey } from './lib.js';
import './riders.css';

/**
 * Ops · rider profile: who the rider is, what they ride, and who to call in an emergency
 * (GET /ops/riders/:userId/profile; the emergency contact is Ops-only). A side sheet; when the
 * list row is passed, its live numbers (status, deliveries, cash) lead.
 */
export function RiderProfileDrawer({
  userId,
  name,
  rider,
  cashLimit,
  onClose,
}: {
  userId: string | null;
  name: string | null;
  rider?: OpsRider | null;
  cashLimit?: number | null;
  onClose(): void;
}) {
  const q = useQuery({ queryKey: riderProfileKey(userId ?? ''), queryFn: () => api.opsRiderProfile(userId ?? ''), enabled: !!userId });
  const v = q.data ? profileView(q.data, { name }) : null;
  const over = !!rider && cashLimit !== null && cashLimit !== undefined && rider.cashOnHandCentavos > cashLimit;

  return (
    <Sheet open={userId !== null} title={v?.title ?? name ?? 'Rider'} onClose={onClose}>
      {v ? (
        <div className="rp-head">
          <RiderPhoto url={v.photoUrl} initials={v.initials} name={v.title} />
          <div className="stack s4">
            {v.incomplete ? <StatusPill tone="warning">Profile incomplete</StatusPill> : <StatusPill tone="success">Profile complete</StatusPill>}
            <span className="dv-sub">{v.updated}</span>
          </div>
        </div>
      ) : null}

      {rider ? (
        <div className="rp-live">
          <div>
            <span className="dv-label">Status</span>
            <span className="rp-status">
              <span className={!rider.online ? 'rd-st off' : rider.activeDeliveries ? 'rd-st busy' : 'rd-st'} aria-hidden="true" />
              {!rider.online ? 'Offline' : rider.activeDeliveries ? 'Busy' : 'Free'}
            </span>
          </div>
          <div>
            <span className="dv-label">On the road</span>
            <Fig text={count(rider.activeDeliveries)} size="md" />
          </div>
          <div className={over ? 'bad' : ''}>
            <span className="dv-label">Cash on hand</span>
            <Fig text={peso(rider.cashOnHandCentavos)} size="md" />
          </div>
        </div>
      ) : null}

      {q.isError && !q.data ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : !v ? (
        <Loading label="Loading the rider" rows={3} />
      ) : (
        <>
          <section className="card dv-card" aria-labelledby="rp-about-h">
            <div className="dv-card-h">
              <h3 id="rp-about-h">Rider and vehicle</h3>
            </div>
            <dl className="rp-facts">
              {profileFacts(v).map((f) => (
                <div key={f.key}>
                  <dt>{f.label}</dt>
                  <dd>{f.value ? f.href ? <a href={f.href}>{f.value}</a> : f.value : <span className="muted">Not set</span>}</dd>
                </div>
              ))}
              <div>
                <dt>Plate</dt>
                <dd>{v.plate ? <span className="rp-plate">{v.plate}</span> : <span className="muted">{v.plateNote ?? 'Not set'}</span>}</dd>
              </div>
            </dl>
          </section>

          <section className="card dv-card" aria-labelledby="rp-ec-h">
            <div className="dv-card-h">
              <h3 id="rp-ec-h">Emergency contact</h3>
            </div>
            {v.emergency.set ? (
              <div className="rp-ec">
                <span className="dv-disc" aria-hidden="true">
                  <Icon name="person" />
                </span>
                <span className="stack s4 grow">
                  <b>{v.emergency.name ?? 'No name given'}</b>
                  <span className="rp-phone">{v.emergency.phone ?? 'No number given'}</span>
                </span>
                {v.emergency.phoneHref ? (
                  <a className="btn btn-quiet" href={v.emergency.phoneHref} aria-label={`Call ${v.emergency.name ?? 'the emergency contact'}`}>
                    Call
                  </a>
                ) : null}
              </div>
            ) : (
              <div className="dv-quiet" title="The rider adds one in the rider app, under Profile.">
                <Icon name="alert" />
                No emergency contact yet
              </div>
            )}
          </section>
        </>
      )}
    </Sheet>
  );
}

/** The rider's photo (signed URL), or their initials when there's none or it fails to load. */
function RiderPhoto({ url, initials, name }: { url: string | null; initials: string; name: string }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [url]);
  if (url && !broken) return <img className="rp-photo" src={url} alt={`Photo of ${name}`} onError={() => setBroken(true)} />;
  return (
    <span className="avatar rp-photo" aria-hidden="true">
      {initials}
    </span>
  );
}
